import { faAws, faDocker, faGithub, faLinkedin, faMedium, faTwitter } from '@fortawesome/free-brands-svg-icons';
import { faArrowRight, faEnvelope } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { gsap } from 'gsap';
import React, { useEffect, useRef } from 'react';
import ContactWidget from '../components/ContactWidget';
import Footer from '../components/Footer';
import MostRecentProjects from '../components/MostRecentProjects';
import Navbar from '../components/navBar';
import RecentArticles from '../components/RecentArticles';
import SocialLink from '../components/SocialLink';

function Homepage() {
  const headingRef = useRef(null);
  const nameRef = useRef(null);
  const descriptionRef = useRef(null);
  const buttonsRef = useRef(null);

  useEffect(() => {
    // Main heading animation
    gsap.from(headingRef.current, {
      y: -50,
      opacity: 0,
      duration: 1,
      ease: "power3.out",
      delay: 0.5
    });

    // Name animation with highlight effect
    gsap.from(nameRef.current, {
      y: 30,
      opacity: 0,
      duration: 1.2,
      ease: "elastic.out(1, 0.75)",
      delay: 0.8
    });

    // Description paragraph fade in
    gsap.from(descriptionRef.current, {
      opacity: 0,
      duration: 1.5,
      ease: "power2.out",
      delay: 1.2
    });

    // Buttons staggered animation - updated to handle complex structure
    gsap.fromTo(
      buttonsRef.current.querySelectorAll('a'),
      { 
        opacity: 0,
        y: 20,
        pointerEvents: 'none' 
      },
      { 
        opacity: 1,
        y: 0,
        pointerEvents: 'auto',
        duration: 0.8,
        stagger: 0.2,
        ease: "back.out(1.7)",
        delay: 1.5,
        clearProps: "all"
      }
    );

    // Social icons animation - using DOM querying instead of refs
    const socialIconsDesktop = document.querySelectorAll('.social-icon-desktop');
    const socialIconsMobile = document.querySelectorAll('.social-icon-mobile');
    
    gsap.from(socialIconsDesktop, {
      opacity: 0,
      x: -20,
      duration: 0.5,
      stagger: 0.15,
      ease: "power1.out",
      delay: 2
    });
    
    gsap.from(socialIconsMobile, {
      opacity: 0,
      y: 20,
      duration: 0.5,
      stagger: 0.15,
      ease: "power1.out",
      delay: 2
    });
  }, []);

  return (
    <div className="relative bg-white dark:bg-gray-900 text-gray-900 dark:text-white min-h-screen overflow-hidden">
      {/* Decorative pattern elements */}
      <div className="absolute top-0 right-0 w-1/3 h-full overflow-hidden opacity-10 pointer-events-none">
        <div className="absolute -right-20 top-20 w-80 h-80 rounded-full bg-[#8f00ff]/20 dark:bg-[#8f00ff]/30 blur-3xl"></div>
        <div className="absolute right-40 top-60 w-64 h-64 rounded-full bg-[#ffd700]/30 dark:bg-[#ffd700]/40 blur-3xl"></div>
      </div>
      <div className="absolute top-1/4 left-0 w-1/4 h-full overflow-hidden opacity-10 pointer-events-none">
        <div className="absolute -left-10 top-40 w-72 h-72 rounded-full bg-[#8f00ff]/20 dark:bg-[#8f00ff]/30 blur-3xl"></div>
        <div className="absolute left-20 top-96 w-60 h-60 rounded-full bg-[#ffd700]/30 dark:bg-[#ffd700]/40 blur-3xl"></div>
      </div>
      
      {/* Main hero section */}
      <div className="min-h-screen relative">
        <div className="relative z-10">
          <Navbar />
          <main className="container mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between min-h-[calc(100vh-5rem)] py-12">
              {/* Left column: Text content */}
              <div className="w-full md:w-1/2 mb-12 md:mb-0">
                <div className="max-w-xl">
                  <h1 className="text-4xl sm:text-5xl md:text-7xl font-bold mb-6 tracking-tight leading-tight">
                    <span ref={headingRef} className="block overflow-hidden">HEY, I'M</span>
                    <span ref={nameRef} className="block bg-gradient-to-r from-[#8f00ff] to-[#ffd700] bg-clip-text text-transparent">
                      OLAWALE FILANI
                    </span>
                  </h1>
                  
                  <p ref={descriptionRef} className="text-lg sm:text-xl text-gray-700 dark:text-gray-300 leading-relaxed mb-8 border-l-4 border-[#8f00ff] pl-4">
                    A Software Engineer with a passion for building impactful mobile and web applications.
                    I specialize in integrating advanced AI models to create user-friendly solutions that help businesses meet their clients' expectations.
                  </p>

                  <div ref={buttonsRef} className="flex flex-row gap-3 mt-8 relative z-20 justify-center sm:justify-start">
                    <a 
                      href="/projects" 
                      className="group w-auto relative overflow-hidden px-3 py-2 text-center rounded-lg bg-gradient-to-r from-[#8f00ff]/10 to-[#ffd700]/10 backdrop-blur-sm border-2 border-[#8f00ff]/50 dark:border-[#ffd700]/50 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] opacity-0 group-hover:opacity-90 transition-opacity duration-300"></div>
                      <div className="flex items-center justify-between relative z-10 w-[100px] sm:w-[120px]">
                        <span className="text-xs sm:text-sm font-bold text-[#8f00ff] dark:text-[#ffd700] group-hover:text-white transition-colors duration-300">Projects</span>
                        <FontAwesomeIcon 
                          icon={faArrowRight} 
                          className="ml-1 sm:ml-2 text-xs transform translate-x-0 group-hover:translate-x-1 transition-transform duration-300 text-[#8f00ff] dark:text-[#ffd700] group-hover:text-white"
                        />
                      </div>
                    </a>
                    
                    <a 
                      href="contact" 
                      className="group w-auto relative overflow-hidden px-3 py-2 text-center rounded-lg bg-gradient-to-r from-[#8f00ff]/10 to-[#ffd700]/10 backdrop-blur-sm border-2 border-[#8f00ff]/50 dark:border-[#ffd700]/50 shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1"
                    >
                      <div className="absolute inset-0 bg-gradient-to-r from-[#8f00ff] to-[#ffd700] opacity-0 group-hover:opacity-90 transition-opacity duration-300"></div>
                      <div className="flex items-center justify-between relative z-10 w-[100px] sm:w-[120px]">
                        <span className="text-xs sm:text-sm font-bold text-[#8f00ff] dark:text-[#ffd700] group-hover:text-white transition-colors duration-300">Contact</span>
                        <FontAwesomeIcon 
                          icon={faEnvelope} 
                          className="ml-1 sm:ml-2 text-xs transform scale-100 group-hover:scale-110 transition-transform duration-300 text-[#8f00ff] dark:text-[#ffd700] group-hover:text-white"
                        />
                      </div>
                    </a>
                  </div>
                </div>
              </div>
              
              {/* Right column: Profile image with decorative elements */}
              <div className="w-full md:w-1/2 relative">
                <div className="relative mx-auto w-64 h-64 sm:w-80 sm:h-80 lg:w-96 lg:h-96">
                  {/* Decorative circles */}
                  <div className="absolute -z-10 w-full h-full rounded-full bg-gradient-to-br from-[#8f00ff]/20 to-[#ffd700]/20 dark:from-[#8f00ff]/30 dark:to-[#ffd700]/30 animate-pulse"></div>
                  <div className="absolute -z-10 top-4 left-4 w-12 h-12 rounded-full bg-[#ffd700]/40 dark:bg-[#ffd700]/50"></div>
                  <div className="absolute -z-10 bottom-12 right-0 w-20 h-20 rounded-full bg-[#8f00ff]/30 dark:bg-[#8f00ff]/40"></div>
                  
                  {/* Profile image */}
                  <div className="relative w-full h-full rounded-full border-4 border-white dark:border-gray-800 shadow-xl overflow-hidden">
                    <img 
                      src="/photo3.png" 
                      alt="Olawale Filani" 
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://via.placeholder.com/400x400?text=Olawale+Filani';
                      }}
                    />
                  </div>

                  {/* Skill badges */}
                  <div className="absolute -top-6 -right-6 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nextjs/nextjs-original.svg" alt="Next.js" className="w-8 h-8" />
                  </div>
                  <div className="absolute top-0 right-1/4 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/typescript/typescript-original.svg" alt="TypeScript" className="w-8 h-8" />
                  </div>
                  <div className="absolute top-1/4 -right-8 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/python/python-original.svg" alt="Python" className="w-8 h-8" />
                  </div>
                  <div className="absolute bottom-1/4 -right-8 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/java/java-original.svg" alt="Java" className="w-8 h-8" />
                  </div>
                  <div className="absolute -bottom-6 right-1/4 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/nodejs/nodejs-original.svg" alt="Node.js" className="w-8 h-8" />
                  </div>
                  <div className="absolute -bottom-6 -left-6 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <div className="w-8 h-8 flex items-center justify-center text-[#FF9900]">
                      <FontAwesomeIcon icon={faAws} className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="absolute top-1/3 -left-8 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <div className="w-8 h-8 flex items-center justify-center text-[#2496ED]">
                      <FontAwesomeIcon icon={faDocker} className="w-6 h-6" />
                    </div>
                  </div>
                  <div className="absolute top-5/6 -right-6 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <img 
                      src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/spring/spring-original.svg" 
                      alt="Spring Boot" 
                      className="w-8 h-8"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://spring.io/img/favicon.ico';
                      }}
                    />
                  </div>
                  <div className="absolute top-2/3 -left-10 bg-white dark:bg-gray-800 rounded-full p-3 shadow-lg border border-gray-100 dark:border-gray-700 transition-transform hover:scale-110 duration-300">
                    <img 
                      src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/postgresql/postgresql-original.svg" 
                      alt="PostgreSQL"
                      className="w-8 h-8"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src = 'https://www.postgresql.org/media/img/about/press/elephant.png';
                      }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </main>

          <div className="fixed left-6 top-1/2 -translate-y-1/2 hidden lg:flex flex-col space-y-7">
            <SocialLink href="#" icon={faTwitter} className="social-icon-desktop text-[#8f00ff] dark:text-[#ffd700] hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300" />
            <SocialLink href="#" icon={faLinkedin} className="social-icon-desktop text-[#8f00ff] dark:text-[#ffd700] hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300" />
            <SocialLink href="https://github.com/olawale021" icon={faGithub} className="social-icon-desktop text-[#8f00ff] dark:text-[#ffd700] hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300" />
            <SocialLink href="#" icon={faMedium} className="social-icon-desktop text-[#8f00ff] dark:text-[#ffd700] hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300" />
          </div>

          <div className="lg:hidden flex justify-center space-x-6 pb-8">
            <SocialLink href="#" icon={faTwitter} className="social-icon-mobile text-[#8f00ff] dark:text-[#ffd700] hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300" />
            <SocialLink href="#" icon={faLinkedin} className="social-icon-mobile text-[#8f00ff] dark:text-[#ffd700] hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300" />
            <SocialLink href="https://github.com/olawale021" icon={faGithub} className="social-icon-mobile text-[#8f00ff] dark:text-[#ffd700] hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300" />
            <SocialLink href="#" icon={faMedium} className="social-icon-mobile text-[#8f00ff] dark:text-[#ffd700] hover:text-[#ffd700] dark:hover:text-[#8f00ff] transition-colors duration-300" />
          </div>
        </div>
      </div>

      <div id="recent-projects">
        <MostRecentProjects />
      </div>
      <div id="articles">
        <RecentArticles />
      </div>
      <div id="contact">
        <ContactWidget />
      </div>
        <Footer />
    </div>
  );
}

export default Homepage;